import React, { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Text, RoundedBox, useTexture } from "@react-three/drei";
import { useXRInputSourceState } from "@react-three/xr";
import * as THREE from "three";
import { useStore, getEffectiveWeather } from "../store";

const GOLD = "#D4AF37";
const GOLD_DIM = "#997316";

function Btn({ position, size = [0.2, 0.09, 0.02], label, active, onClick, color = GOLD }) {
  const [hover, setHover] = useState(false);
  return (
    <group position={position}>
      <RoundedBox
        args={size}
        radius={0.015}
        smoothness={3}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={() => setHover(true)}
        onPointerOut={() => setHover(false)}
      >
        <meshStandardMaterial
          color={active ? color : hover ? "#3a3f49" : "#20242c"}
          emissive={active ? color : "#000"}
          emissiveIntensity={active ? 0.4 : 0}
          transparent
          opacity={0.92}
          metalness={0.3}
          roughness={0.4}
        />
      </RoundedBox>
      <Text position={[0, 0, 0.02]} fontSize={0.032} color={active ? "#0b0c10" : "#ece8e1"} anchorX="center" anchorY="middle">
        {label}
      </Text>
    </group>
  );
}

function WristMenu() {
  const left = useXRInputSourceState("controller", "left");
  const ref = useRef();
  const seal = useTexture("/tab-seal.png");
  const muted = useStore((s) => s.muted);
  const navMode = useStore((s) => s.navMode);
  const modes = ["orbit", "walk", "bridge", "hudson", "helicopter"];
  useFrame(() => {
    const obj = left?.object;
    if (obj && ref.current) {
      ref.current.visible = true;
      obj.updateWorldMatrix(true, false);
      ref.current.matrix.copy(obj.matrixWorld);
      ref.current.matrix.decompose(ref.current.position, ref.current.quaternion, ref.current.scale);
      ref.current.translateY(0.08);
      ref.current.rotateX(-0.9);
    } else if (ref.current) {
      ref.current.visible = false;
    }
  });
  return (
    <group ref={ref} matrixAutoUpdate={false} visible={false}>
      <RoundedBox args={[0.34, 0.16, 0.01]} radius={0.02} smoothness={3} position={[0, 0, -0.005]}>
        <meshStandardMaterial color="#14161d" transparent opacity={0.8} metalness={0.2} roughness={0.5} />
      </RoundedBox>
      <Btn position={[-0.08, 0.02, 0.01]} size={[0.14, 0.07, 0.015]} label={muted ? "UNMUTE" : "MUTE"} active={!muted} onClick={() => useStore.getState().toggleMuted()} />
      <Btn
        position={[0.08, 0.02, 0.01]}
        size={[0.14, 0.07, 0.015]}
        label={navMode.toUpperCase().slice(0, 5)}
        active
        color={GOLD_DIM}
        onClick={() => {
          const i = modes.indexOf(navMode);
          useStore.getState().setNavMode(modes[(i + 1) % modes.length]);
        }}
      />
      <Text position={[0, -0.055, 0.01]} fontSize={0.022} color={GOLD} anchorX="center">
        WRIST CONTROLS
      </Text>
      <mesh position={[0, 0.095, 0.01]}>
        <circleGeometry args={[0.028, 32]} />
        <meshBasicMaterial map={seal} toneMapped={false} />
      </mesh>
    </group>
  );
}

export default function VRPanel() {
  const panel = useRef();
  const thumb = useRef();
  const drag = useRef(false);
  const TW = 0.9;
  const YEAR_MIN = 2001;
  const YEAR_MAX = 2026;

  const setFromWorldX = (worldPoint) => {
    if (!panel.current) return;
    const local = panel.current.worldToLocal(worldPoint.clone());
    const u = THREE.MathUtils.clamp((local.x + TW / 2) / TW, 0, 1);
    useStore.getState().setTemporal(YEAR_MIN + u * (YEAR_MAX - YEAR_MIN));
  };

  useFrame(() => {
    const t = useStore.getState().temporal;
    const u = (t - YEAR_MIN) / (YEAR_MAX - YEAR_MIN);
    if (thumb.current) thumb.current.position.x = -TW / 2 + u * TW;
  });

  return (
    <>
      <WristMenu />
      <group ref={panel} position={[0, 1.45, -1.7]}>
        {/* glass backdrop */}
        <RoundedBox
          args={[1.25, 0.62, 0.02]}
          radius={0.04}
          smoothness={4}
          position={[0, 0, -0.02]}
          onPointerMove={(e) => {
            if (drag.current) {
              e.stopPropagation();
              setFromWorldX(e.point);
            }
          }}
          onPointerUp={() => (drag.current = false)}
        >
          <meshPhysicalMaterial color="#0e1622" transparent opacity={0.55} transmission={0.4} thickness={0.4} roughness={0.25} metalness={0.1} />
        </RoundedBox>
        <RoundedBox args={[1.25, 0.62, 0.021]} radius={0.04} smoothness={4} position={[0, 0, -0.03]}>
          <meshBasicMaterial color={GOLD} wireframe transparent opacity={0.25} />
        </RoundedBox>

        <Text position={[0, 0.22, 0.01]} fontSize={0.06} color="#f4e3a1" anchorX="center" letterSpacing={0.05}>
          TEMPORAL BRIDGE
        </Text>

        {/* slider track */}
        <mesh
          position={[0, 0.0, 0.01]}
          onPointerDown={(e) => {
            e.stopPropagation();
            drag.current = true;
            setFromWorldX(e.point);
          }}
        >
          <boxGeometry args={[TW, 0.05, 0.02]} />
          <meshStandardMaterial color="#2a2f38" metalness={0.3} roughness={0.5} />
        </mesh>
        <mesh position={[0, 0.0, 0.015]} scale={[1, 1, 1]}>
          <boxGeometry args={[TW, 0.012, 0.021]} />
          <meshStandardMaterial color={GOLD} emissive={GOLD} emissiveIntensity={0.6} />
        </mesh>
        <mesh ref={thumb} position={[TW / 2, 0, 0.03]}>
          <sphereGeometry args={[0.05, 24, 24]} />
          <meshStandardMaterial color={GOLD} emissive={GOLD} emissiveIntensity={0.9} metalness={0.6} roughness={0.2} />
        </mesh>

        <Text position={[-TW / 2, -0.12, 0.01]} fontSize={0.045} color="#e6ddc4" anchorX="center">
          2001
        </Text>
        <Text position={[TW / 2, -0.12, 0.01]} fontSize={0.045} color="#e6ddc4" anchorX="center">
          2026
        </Text>

        <Btn position={[-0.32, -0.24, 0.02]} label="ORIGINAL 2001" onClick={() => useStore.getState().setTemporal(2001)} />
        <Btn position={[0.32, -0.24, 0.02]} label="MEMORIAL 2026" onClick={() => useStore.getState().setTemporal(2026)} />
      </group>
    </>
  );
}
